// src/components/ProgressDashboard.js
import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { db, collection, getDocs, onSnapshot, doc, query, where } from '../firebase/firebase';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { cn } from '../lib/utils';
import { AuthContext } from '../App'; // Assuming AuthContext is exported from App.js

const ProgressDashboard = ({ allProblems, isTeacher, dailyProblemId, onClose }) => { // Accept dailyProblemId and onClose props
    const currentUser = useContext(AuthContext);
    const teacherId = currentUser ? currentUser.uid : null;

    // Helper function to check if a problem is the daily problem and return star icon
    const getDailyProblemIndicator = React.useCallback((problemId) => {
        if (problemId === dailyProblemId) {
            return <span className="text-yellow-400 ml-1" title="Daily Problem">⭐</span>;
        }
        return null;
    }, [dailyProblemId]);

    const [allUsers, setAllUsers] = useState([]); // All registered users (students)
    const [allSections, setAllSections] = useState([]); // All sections managed by this teacher
    const [selectedProblemId, setSelectedProblemId] = useState(''); // The problem currently being displayed
    const [selectedSectionId, setSelectedSectionId] = useState(''); // The section filter ('All' or specific section)
    const [studentProgressMap, setStudentProgressMap] = useState({}); // { studentUid: { problemId: { progressData } } }
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showStudentNames, setShowStudentNames] = useState(false); // Toggle for showing/hiding student names

    // Ref to store unsubscribe functions for real-time listeners
    const unsubscribeListenersRef = useRef([]);

    // Filter students based on selected section
    const filteredStudents = React.useMemo(() => {
        if (!selectedSectionId || selectedSectionId === 'All') {
            return allUsers; // Show all students
        }
        const selectedSection = allSections.find(section => section.id === selectedSectionId);
        if (!selectedSection || !selectedSection.studentUids) {
            return []; // No students in this section
        }
        // Filter allUsers to only include students in the selected section
        return allUsers.filter(user => selectedSection.studentUids.includes(user.uid));
    }, [allUsers, allSections, selectedSectionId]);

    // --- Helper function to fetch all registered users ---
    const fetchAllUsers = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const usersCollectionRef = collection(db, 'users');
            const q = collection(db, 'users'); // Fetch all users
            const querySnapshot = await getDocs(q);
            const loadedUsers = [];
            querySnapshot.forEach(doc => {
                const userData = doc.data();
                // Only include non-teachers in the student list
                if (userData.role !== 'teacher') {
                    loadedUsers.push({ uid: doc.id, email: userData.email, displayName: userData.displayName });
                }
            });
            setAllUsers(loadedUsers);

        } catch (fetchError) {
            console.error("Error fetching all users for dashboard:", fetchError);
            setError(`Failed to load user list: ${fetchError.message}`);
        } finally {
            setLoading(false);
        }
    }, []);

    // --- Helper function to fetch sections managed by this teacher ---
    const fetchSections = useCallback(async () => {
        if (!teacherId) return;
        
        setLoading(true);
        setError(null);
        try {
            const sectionsCollectionRef = collection(db, 'sections');
            const q = query(sectionsCollectionRef, where('teacherId', '==', teacherId));
            const querySnapshot = await getDocs(q);
            const loadedSections = [];
            querySnapshot.forEach(doc => {
                loadedSections.push({ id: doc.id, ...doc.data() });
            });
            // Sort by creation date
            loadedSections.sort((a, b) => (a.createdAt?.toDate() || 0) - (b.createdAt?.toDate() || 0));
            setAllSections(loadedSections);

        } catch (fetchError) {
            console.error("Error fetching sections for dashboard:", fetchError);
            setError(`Failed to load sections: ${fetchError.message}`);
        } finally {
            setLoading(false);
        }
    }, [teacherId]);

    // --- Real-time listener for student progress on the selected problem ---
    useEffect(() => {
        // Clear previous listeners
        unsubscribeListenersRef.current.forEach(unsubscribe => unsubscribe());
        unsubscribeListenersRef.current = [];
        setStudentProgressMap({}); // Reset progress map when problem changes

        if (!selectedProblemId || filteredStudents.length === 0) {
            return;
        }



        filteredStudents.forEach(student => {
            const problemDocRef = doc(db, `users/${student.uid}/problems_solved`, selectedProblemId);
            const unsubscribe = onSnapshot(problemDocRef, (docSnap) => {
                setStudentProgressMap(prev => {
                    const newMap = { ...prev };
                    if (docSnap.exists()) {
                        newMap[student.uid] = docSnap.data();
                    } else {
                        // If document doesn't exist, set default progress (not attempted)
                        newMap[student.uid] = {
                            completed: false,
                            passedTests: 0,
                            totalTests: allProblems.find(p => p.id === selectedProblemId)?.tests?.length || 0,
                            savedCode: null
                        };
                    }
                    return newMap;
                });
            }, (err) => {
                console.error(`Error listening to student ${student.uid}'s progress for problem ${selectedProblemId}:`, err);
                // Optionally show a more specific error for this student/problem
            });
            unsubscribeListenersRef.current.push(unsubscribe);
        });

        // Cleanup function for when component unmounts or dependencies change
        return () => {
            unsubscribeListenersRef.current.forEach(unsubscribe => unsubscribe());
            unsubscribeListenersRef.current = [];
        };
    }, [selectedProblemId, filteredStudents, allProblems]); // Re-run when selected problem or filtered student list changes

    // --- Initial data fetch on component mount ---
    useEffect(() => {
        if (isTeacher) {
            fetchAllUsers();
            fetchSections();
        } else {
            setLoading(false);
            setError("You must be a teacher to view the Progress Dashboard.");
        }
    }, [isTeacher, fetchAllUsers, fetchSections]);

    // This useEffect handles changes to dailyProblemId after initial load
    // It also sets the initial selected problem if dailyProblemId is available
    useEffect(() => {
        if (isTeacher && allProblems.length > 0) {
            if (dailyProblemId && allProblems.some(p => p.id === dailyProblemId)) {
                // If dailyProblemId exists, set it
                if (selectedProblemId === '' || (selectedProblemId === dailyProblemId && selectedProblemId !== dailyProblemId)) {
                     setSelectedProblemId(dailyProblemId);
                }
            } else if (selectedProblemId === '' && allProblems.length > 0) {
                // If no dailyProblemId, and no problem is selected, default to the first problem
                setSelectedProblemId(allProblems[0].id);
            }
        }
    }, [dailyProblemId, allProblems, isTeacher, selectedProblemId]);


    // --- Render Logic ---
    if (!isTeacher) {
        return (
            <Card className="bg-mad-card-bg backdrop-blur-md border border-mad-border-color shadow-lg p-6 text-center rounded-xl">
                <CardTitle className="text-xl text-mad-orange mb-2">Access Denied</CardTitle>
                <CardDescription className="text-gray-400">You do not have permission to view the Progress Dashboard.</CardDescription>
            </Card>
        );
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center p-8">
                <Loader2 className="animate-spin w-8 h-8 text-mad-yellow-accent" />
                <span className="ml-3 text-lg text-gray-300">Loading dashboard...</span>
            </div>
        );
    }

    if (error) {
        return (
            <Card className="bg-red-900/20 backdrop-blur-md border border-red-500/50 shadow-lg p-6 text-center rounded-xl">
                <CardTitle className="text-xl text-red-400 mb-2">Error</CardTitle>
                <CardDescription className="text-red-300">{error}</CardDescription>
            </Card>
        );
    }

    const currentProblem = allProblems.find(p => p.id === selectedProblemId);
    if (!currentProblem && selectedProblemId) {
        return (
             <Card className="bg-red-900/20 backdrop-blur-md border border-red-500/50 shadow-lg p-6 text-center rounded-xl">
                <CardTitle className="text-xl text-red-400 mb-2">Problem Not Found</CardTitle>
                <CardDescription className="text-red-300">The selected problem could not be found in the problem list.</CardDescription>
            </Card>
        );
    }

    // Determine max tests for scaling the Y-axis
    const maxTests = currentProblem ? currentProblem.tests?.length || 1 : 1; // Ensure at least 1 for division

    return (
        <Card className="bg-mad-card-bg backdrop-blur-md border border-mad-border-color shadow-lg p-6 rounded-xl">
            <CardHeader className="mb-4">
                <div className="flex items-center justify-between mb-4">
                    <CardTitle className="text-2xl text-mad-yellow-accent">Real-time Progress Dashboard</CardTitle>
                    {onClose && (
                        <Button
                            onClick={onClose}
                            variant="ghost"
                            size="sm"
                            className="text-mad-peel hover:text-white hover:bg-mad-peel/20 rounded-full px-3 py-1 text-xs"
                            title="Return to solve view"
                        >
                            ← Back to Problems
                        </Button>
                    )}
                </div>
                
                {/* Filters and controls on one line */}
                <div className="flex flex-wrap items-center gap-4 mb-4">
                    <div className="flex items-center gap-2">
                        <label htmlFor="section-select" className="text-mad-timberwolf text-sm whitespace-nowrap">Section:</label>
                        <select
                            id="section-select"
                            value={selectedSectionId}
                            onChange={(e) => setSelectedSectionId(e.target.value)}
                            className="bg-mad-gunmetal text-mad-timberwolf border border-mad-border-color rounded-md py-1.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-mad-orange"
                        >
                            <option value="">All Students</option>
                            {allSections.map(section => (
                                <option key={section.id} value={section.id}>
                                    {section.name} ({section.studentUids?.length || 0})
                                </option>
                            ))}
                        </select>
                    </div>
                    
                    <div className="flex items-center gap-2">
                        <label htmlFor="problem-select" className="text-mad-timberwolf text-sm whitespace-nowrap">Problem:</label>
                        <select
                            id="problem-select"
                            value={selectedProblemId}
                            onChange={(e) => setSelectedProblemId(e.target.value)}
                            className="bg-mad-gunmetal text-mad-timberwolf border border-mad-border-color rounded-md py-1.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-mad-orange"
                        >
                            {allProblems.length === 0 ? (
                                <option value="">No problems available</option>
                            ) : (
                                allProblems.map(problem => (
                                    <option key={problem.id} value={problem.id}>
                                        {problem.title}{problem.id === dailyProblemId ? ' ⭐' : ''}
                                    </option>
                                ))
                            )}
                        </select>
                    </div>
                    
                    <Button
                        onClick={() => setShowStudentNames(!showStudentNames)}
                        variant="outline"
                        size="sm"
                        className={cn(
                            "text-xs px-3 py-1.5 rounded-md transition-colors whitespace-nowrap",
                            showStudentNames 
                                ? "bg-mad-fulvous text-white border-mad-fulvous hover:bg-mad-fulvous/80" 
                                : "bg-mad-gunmetal text-mad-timberwolf border-mad-border-color hover:bg-mad-gunmetal/80"
                        )}
                    >
                        {showStudentNames ? 'Hide Names' : 'Show Names'}
                    </Button>
                    
                    <span className="text-xs text-mad-timberwolf/70 italic">
                        {filteredStudents.length} student{filteredStudents.length !== 1 ? 's' : ''}
                    </span>
                </div>

                {/* Problem description */}
                {currentProblem && (
                    <div className="bg-mad-gunmetal/50 border border-mad-border-color rounded-lg p-4 mb-2">
                        <div className="flex items-start justify-between mb-2">
                            <h3 className="text-lg font-semibold text-mad-fulvous flex items-center gap-2">
                                {currentProblem.title}
                                {getDailyProblemIndicator(currentProblem.id)}
                            </h3>
                        </div>
                        <p className="text-mad-timberwolf text-sm whitespace-pre-wrap">
                            {currentProblem.description}
                        </p>
                        {currentProblem.example && (
                            <p className="text-mad-peel text-sm mt-2">
                                <strong>Example:</strong> {currentProblem.example}
                            </p>
                        )}
                    </div>
                )}
            </CardHeader>
            <CardContent>
                {filteredStudents.length === 0 ? (
                    <p className="text-mad-timberwolf italic text-center">
                        {selectedSectionId && selectedSectionId !== 'All' 
                            ? 'No students in the selected section.' 
                            : 'No students registered yet.'}
                    </p>
                ) : (
                    <div className="flex items-end h-[400px] w-full bg-mad-gunmetal p-2 rounded-lg border border-mad-border-color overflow-x-auto">
                        {/* Y-axis labels (simplified) */}
                        <div className="flex flex-col justify-between h-full pr-1 text-mad-timberwolf text-xs text-right pb-6 flex-shrink-0" style={{ width: '30px' }}>
                            <span>{maxTests}</span>
                            <span>{Math.floor(maxTests * 0.75)}</span>
                            <span>{Math.floor(maxTests * 0.5)}</span>
                            <span>{Math.floor(maxTests * 0.25)}</span>
                            <span>0</span>
                        </div>
                        <div className="flex flex-grow items-end h-full gap-1 px-1">
                            {filteredStudents.map(student => {
                                const progress = studentProgressMap[student.uid];
                                const passedTests = progress?.passedTests || 0;
                                const totalTests = progress?.totalTests || (currentProblem?.tests?.length || 0);

                                // Calculate heights as percentages
                                const passedHeight = totalTests > 0 ? (passedTests / totalTests) * 100 : 0;
                                const remainingHeight = 100 - passedHeight; // Total height is 100%

                                // Dynamic width based on number of students (min 32px, max 64px)
                                const barWidth = Math.max(32, Math.min(64, Math.floor((100 - filteredStudents.length * 2) / filteredStudents.length)));

                                return (
                                    <div key={student.uid} className="flex flex-col items-center h-full justify-end flex-shrink-0" style={{ width: `${barWidth + 8}px` }}>
                                        <div 
                                            className="relative flex flex-col justify-end h-full bg-red-700/80 rounded-t-md overflow-hidden"
                                            style={{ width: `${barWidth}px` }}
                                        >
                                            {/* Red part of the bar (remaining/failed tests) */}
                                            {/* This div represents the full bar height, green will overlay */}
                                            
                                            {/* Green part of the bar (passed tests) */}
                                            <div
                                                className="absolute bottom-0 left-0 w-full bg-mad-green rounded-t-md transition-all duration-500 ease-out"
                                                style={{ height: `${passedHeight}%` }}
                                            ></div>
                                            {/* Tooltip/Label for tests passed */}
                                            <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center text-white font-bold text-xs">
                                                {totalTests > 0 ? `${passedTests}/${totalTests}` : 'N/A'}
                                            </div>
                                        </div>
                                        {showStudentNames && (
                                            <div className="mt-1 text-mad-timberwolf text-xs text-center w-full truncate" title={student.displayName || student.email}>
                                                {(student.displayName || student.email.split('@')[0]).substring(0, 8)}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default ProgressDashboard;
